import { http } from '@/lib/http';

export type DeliveryServiceTierCode = 'STANDARD' | 'EXPRESS';
export type CourierOptimise = 'CHEAPEST' | 'FASTEST';
export type DeliveryTierPricingMode = 'STATIC' | 'DYNAMIC';
export type DeliveryFulfilmentMode = 'AUTO_OPTIMIZE' | 'FIXED_PARTNER' | 'RULE_BASED';
/** Destination scope for a per-tier override. (ALL = the base tier.) */
export type TierOverrideScope = 'DOMESTIC' | 'INTERNATIONAL';

/** A per-destination override on a tier. Null fields inherit the base tier. */
export interface DeliveryServiceTierScope {
  id: string;
  tierId: string;
  scope: TierOverrideScope;
  fulfilmentMode: DeliveryFulfilmentMode | null;
  fixedPartnerId: string | null;
  optimise: CourierOptimise | null;
  maxEtaDays: number | null;
  preferAir: boolean | null;
  preferRecommended: boolean | null;
  rates: Record<string, number> | null;
  etaDays: number | null;
}

export interface DeliveryServiceTier {
  id: string;
  tenantId: string;
  tier: DeliveryServiceTierCode;
  enabled: boolean;
  label: string;
  etaDays: number | null;
  /** Per-currency price map, e.g. { INR: 50, USD: 3 }. Static charge, or
   *  the floor + offline fallback in DYNAMIC mode. */
  rates: Record<string, number>;
  // Fulfilment control
  fulfilmentMode: DeliveryFulfilmentMode;
  /** Pinned partner when fulfilmentMode = FIXED_PARTNER; null otherwise. */
  fixedPartnerId: string | null;
  // Pricing (Phase 2)
  pricingMode: DeliveryTierPricingMode;
  /** Margin % on the live courier rate (12 = +12%). */
  marginPct: number;
  /** Round the price up to the nearest multiple (0 = none). */
  roundTo: number;
  /** Free delivery when subtotal (base currency) ≥ this. Null = never. */
  freeAboveSubtotal: number | null;
  // Courier selection policy
  optimise: CourierOptimise;
  maxEtaDays: number | null;
  preferAir: boolean;
  preferRecommended: boolean;
  sortOrder: number;
  createdAt: string;
  updatedAt: string;
  /** Per-destination strategy overrides (Domestic / International). */
  scopeOverrides?: DeliveryServiceTierScope[];
}

/** One per-destination override sent on save. Omit a field to inherit base. */
export interface DeliveryTierScopePayload {
  scope: TierOverrideScope;
  fulfilmentMode?: DeliveryFulfilmentMode;
  fixedPartnerId?: string;
  optimise?: CourierOptimise;
  maxEtaDays?: number;
  preferAir?: boolean;
  preferRecommended?: boolean;
  rates?: Record<string, number>;
  etaDays?: number;
}

export interface UpsertDeliveryTierPayload {
  enabled?: boolean;
  label?: string;
  etaDays?: number;
  rates?: Record<string, number>;
  fulfilmentMode?: DeliveryFulfilmentMode;
  fixedPartnerId?: string;
  pricingMode?: DeliveryTierPricingMode;
  marginPct?: number;
  roundTo?: number;
  freeAboveSubtotal?: number | null;
  optimise?: CourierOptimise;
  maxEtaDays?: number;
  preferAir?: boolean;
  preferRecommended?: boolean;
  /** Per-destination overrides. Absent = leave untouched; [] = clear all;
   *  otherwise the listed scopes are upserted and unlisted scopes removed. */
  scopes?: DeliveryTierScopePayload[];
}

const BASE = '/api/v1/dispatch/delivery-tiers';

export const deliveryTiersService = {
  /** Lists the tenant's tiers, seeding STANDARD + EXPRESS rows on first read. */
  list: () => http.get<DeliveryServiceTier[]>(BASE),
  upsert: (tier: DeliveryServiceTierCode, payload: UpsertDeliveryTierPayload) =>
    http.put<DeliveryServiceTier, UpsertDeliveryTierPayload>(`${BASE}/${tier}`, payload),
};
